import React, { useState, useEffect, useContext } from 'react';
import axios from "axios";
import { GlobalContext } from '../contexts/GlobalContext';
import { 
    Alert, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { hasDeviceNotificationPermissions } from '../utils/devicePermissions';
import BuyerCatalog from '../pages/buyer/Catalog';

export default function DueReminder({invoice, setDueAlertEnabled}) {

  const { url, contact, portal, token, invoiceConfig, timeFormat } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [isReminderAvailable, setIsReminderAvailable] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [remindOn, setRemindOn] = useState(null);

  const [errors, setErrors] = useState({ remindOn: false });

    const checkPermission = () => {
        return new Promise(async (resolve, reject) => {
            if ( hasDeviceNotificationPermissions(contact, portal, ['invoice_due_discounts']) === false ) return resolve(false);
            
            const hasPermission = await axios.post(`${url}api/available-notifications`,{contact, portal}, { headers: { Authorization: `Bearer ${token}` }})
                .then((res) => {
                    if (res.data.error) throw new Error(res.data.message);
                    return res.data.available_notifications;
                })
                .then((availableNotifications) => {
                    if (availableNotifications.includes('invoice_due_discounts') === false) return false;
                    return true;
                })
                .catch((err) => reject(err) );

            resolve(hasPermission);
        })
    };

    const handleDueAlert = (daysRemaining) => {
        const dueAlertDays = parseFloat(invoiceConfig.dueAlertDays) || 0;
        if ((daysRemaining > 0) === false) return;
        setDaysRemaining(daysRemaining);
        if( daysRemaining <= dueAlertDays ) setAlertEnabled(true);
    }

    const fetchAndSetRemindOn = () => {
        axios.post(`${url}api/buyers/invoices/due-reminders`, { invoice: invoice.voucher.id }, { headers: { Authorization: `Bearer ${token}` }})
            .then((res) => {
                if (res.data.error) throw new Error(res.data.message);
                return res.data.reminder;
            })
            .then((reminder) => {
                if (reminder === null) return;
                setReminder(reminder);
            })
            .catch((err) => {
                console.error(err);
                alert("Error fetching reminder!");
            });
    }

    const getLocalDateTimeValue = (dateTime) => {
        const utcDate = new Date(dateTime);
        const pad = n => n.toString().padStart(2, '0');
        return `${utcDate.getFullYear()}-${pad(utcDate.getMonth() + 1)}-${pad(utcDate.getDate())}T${pad(utcDate.getHours())}:${pad(utcDate.getMinutes())}`;
    }

    useEffect(() => reminder?.remind_at ? setRemindOn(getLocalDateTimeValue(reminder.remind_at)) : setRemindOn(null), [reminder])

    useEffect(()=>{
        (async () => {
            if(Boolean(invoice.summary) === false) return;

            if (Boolean(invoice.summary.can_avail_due_discount) === false) return;

            const daysRemaining = invoice.summary.remaining_due_days || null;
            if (daysRemaining === null) return;

            handleDueAlert(daysRemaining);
    
            const hasPermission = await checkPermission();
            if (hasPermission) setIsReminderAvailable( true );

            fetchAndSetRemindOn();
        })();
    }, [invoice]);

    useEffect(() => setDueAlertEnabled(alertEnabled), [alertEnabled])
 
    const handleSubmit = (event) => {
        event.preventDefault();

        const link = reminder ? `${url}api/buyers/invoices/due-reminders/${reminder.id}/update` : `${url}api/buyers/invoices/due-reminders/add`;
        axios.post(link, { invoice: invoice.voucher.id, remind_at: remindOn, contact  }, { headers: { Authorization: `Bearer ${token}` }})
            .then((res) => {
                if (res.data.error) throw new Error(res.data.message);
                return res.data.reminder;
            })
            .then((reminder) => {
                setReminder(reminder);
                alert('Reminder set successfully!');
            })
            .catch((err) => {
                console.error(err);
                alert("Error setting reminder!");
            })
            .finally(() => setIsDialogOpen(false));
    };

    const handleDelete = () => {
        if ( !confirm('Are you sure you want to delete this reminder?') ) return false;

        axios.post(`${url}api/buyers/invoices/due-reminders/${reminder.id}/remove`, { contact }, { headers: { Authorization: `Bearer ${token}` }})
           .then((res) => { if (res.data.error) throw new Error(res.data.message); })
           .then(() => { setReminder(null); alert('Reminder deleted successfully!'); })
           .catch((err) => { console.error(err); alert("Error deleting reminder!"); })
    };

    const handleEditOpen = () => { setRemindOn(getLocalDateTimeValue(reminder.remind_at)); setIsDialogOpen(true); };

    const handleDialogClose = () => { setIsDialogOpen(false); setRemindOn(null); }

    const handleRemindOnChange = (e) => {
        const selectedRemindOn = e.target.value;
        const now = new Date();
        const selectedDate = new Date(selectedRemindOn);
        
        if (selectedDate <= now) {
            setErrors((errors) => ({...errors, remindOn: 'Please select a future date and time'}) );
            return;
        }
        
        setRemindOn(selectedRemindOn);
        setErrors((errors) => ({...errors, remindOn: null}) );
    };

    return (
        <>

        { 
            alertEnabled && 
            <Alert  severity="error">
                {`Only ${daysRemaining} days left for due date discount!`}
            </Alert>
        }
        {
            isReminderAvailable &&
            <Paper sx={{ p: 2, maxWidth: 400, margin: 'auto', mt: 2, mb: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActiveOutlinedIcon sx={{ mr: 1 }}/>
                    <Typography variant="subtitle2">Need a due date reminder?</Typography>
                </div>
                {!!reminder ? 
                    (
                    <div>
                        <IconButton color="primary" size="small" onClick={handleEditOpen} sx={{ mr: 1 }}>
                        <Edit />
                        </IconButton>
                        <IconButton color="error" size="small" onClick={handleDelete}>
                        <Delete />
                        </IconButton>
                    </div>
                    ) : (
                    <Button variant="contained" color="success" onClick={() => setIsDialogOpen(true)}>
                        Set Reminder
                    </Button>
                    )
                }
                </div>

                {reminder && (
                <Typography variant="body2" color="textSecondary" sx={{p: 1}}>
                    Reminder set for: <b>{new Date(reminder.remind_at).toLocaleString('en-GB', timeFormat)}</b>
                </Typography>
                )}

                <Dialog open={isDialogOpen} onClose={handleDialogClose}>
                <DialogTitle>
                    <Typography variant="subtitle2" sx={{fontWeight: "bold", mb: 2}}>Set Reminder</Typography>
                    <Typography variant="body2">Please specify the exact date and time you would like to be notified before the due date</Typography>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                    <TextField
                        label="Date and Time"
                        type="datetime-local"
                        value={remindOn}
                        onChange={handleRemindOnChange}
                        error={!!errors.remindOn}
                        helperText={errors.remindOn}
                        InputLabelProps={{ shrink: true, }}
                        inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                        fullWidth
                    />
                    </DialogContent>
                    <DialogActions sx={{mb: 1}}>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button type="submit" variant="contained">Save</Button>
                    </DialogActions>
                </form>
                </Dialog>
            </Paper>
        }
        </>
    );
}